import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { GoogleG } from "@/components/GoogleG";
import api from "@/lib/api";
import { SITE } from "@/lib/site";

export default function Gallery() {
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [google, setGoogle] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/reviews/google").then((r) => { if (r.data.configured && !r.data.error) setGoogle(r.data); }).catch(() => {});
  }, []);

  const reviewItems = google?.reviews?.length
    ? google.reviews.map((r) => ({ ...r, trip: "Google review" }))
    : testimonials;
  const liveRating = google?.rating ? Number(google.rating).toFixed(1) : "4.8";

  const spans = ["md:col-span-2 md:row-span-2", "", "", "md:row-span-2", "", "md:col-span-2", "", ""];

  return (
    <div className="pt-32 pb-24" data-testid="gallery-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SectionHeading overline="Gallery" title={<>Shot by <span className="font-light italic">our travellers.</span></>} className="mb-14" />
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 auto-rows-[240px] gap-3 mb-24" data-testid="gallery-grid">
          {gallery.map((g, i) => (
            <Reveal key={g.caption} delay={(i % 4) * 0.06} className={spans[i % spans.length]}>
              <div className="relative overflow-hidden rounded-2xl group w-full h-full">
                <img src={g.image} alt={g.caption} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/45 transition-colors duration-500" />
                <p className="absolute bottom-4 left-5 text-bone font-display font-bold opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">{g.caption}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <div className="bg-ink py-24" data-testid="google-reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-3 gap-10 items-start">
            <div className="lg:col-span-1">
              <SectionHeading dark overline="Google Reviews" title={<>Rated by <span className="font-light italic text-bone/70">real travellers.</span></>} />
              <Reveal delay={0.1}>
                <div className="mt-8 bg-bone/5 border border-bone/10 rounded-3xl p-8" data-testid="google-rating-card">
                  <div className="flex items-center gap-3 mb-5">
                    <GoogleG size={30} />
                    <span className="text-bone font-display font-bold text-lg">Google Reviews</span>
                  </div>
                  <p className="font-display text-6xl font-black text-bone" data-testid="google-rating-value">{liveRating}</p>
                  <div className="flex gap-1 my-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={18} className={i < Math.round(Number(liveRating)) ? "fill-sun text-sun" : "text-bone/20"} />
                    ))}
                  </div>
                  <p className="text-bone/50 text-sm mb-7" data-testid="google-ratings-summary">
                    {google?.total ? `Based on ${google.total} ratings posted on Google` : "Based on reviews posted on Google by click2trip travellers"}
                  </p>
                  <div className="flex flex-col gap-3">
                    <a href={SITE.googleReviewUrl} target="_blank" rel="noreferrer" data-testid="read-google-reviews-btn"
                      className="inline-flex items-center justify-center gap-2 bg-bone text-ink text-sm font-semibold px-6 py-3 rounded-full hover:scale-[1.03] transition-transform">
                      <GoogleG size={15} /> Read all reviews
                    </a>
                    <a href={SITE.googleReviewUrl} target="_blank" rel="noreferrer" data-testid="write-google-review-btn"
                      className="inline-flex items-center justify-center gap-2 border border-bone/20 text-bone/80 text-sm font-medium px-6 py-3 rounded-full hover:border-sun hover:text-sun transition-colors">
                      Write a review
                    </a>
                  </div>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5" data-testid="google-reviews-grid">
              {reviewItems.map((t, i) => (
                <Reveal key={t.name} delay={(i % 2) * 0.08}>
                  <div className="bg-bone/5 border border-bone/10 rounded-2xl p-6 h-full flex flex-col" data-testid={`google-review-card-${i}`}>
                    <div className="flex items-center gap-3 mb-4">
                      {t.avatar ? (
                        <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover border-2 border-sun" />
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-sun/20 border-2 border-sun flex items-center justify-center font-display font-bold text-sun">
                          {(t.name || "G")[0]}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-bone font-display font-bold truncate">{t.name}</p>
                        <p className="text-bone/40 text-xs">{t.date}</p>
                      </div>
                      <div className="ml-auto flex gap-0.5 shrink-0">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <Star key={j} size={12} className={j < t.rating ? "fill-sun text-sun" : "text-bone/20"} />
                        ))}
                      </div>
                    </div>
                    <p className="text-bone/70 text-sm leading-relaxed flex-1 line-clamp-5">"{t.text}"</p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-bone/10">
                      <span className="text-bone/40 text-xs truncate">{t.trip}</span>
                      <a href={t.maps_uri || SITE.googleReviewUrl} target="_blank" rel="noreferrer" data-testid={`google-review-source-${i}`}
                        className="inline-flex items-center gap-1.5 text-[11px] text-bone/60 hover:text-bone transition-colors shrink-0 ml-2">
                        <GoogleG size={12} /> Posted on Google
                      </a>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
